import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../context/StoreContext';
import { campaignsAPI, analyticsAPI, extractErrorMessage } from '../api';

const Campaigns = () => {
  const { selectedStore } = useStore();

  // Campaign list state
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);

  // Dead stock products for selection
  const [deadStockProducts, setDeadStockProducts] = useState([]);
  const [loadingDeadStock, setLoadingDeadStock] = useState(false);

  // Performance view state
  const [performanceCampaign, setPerformanceCampaign] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [loadingPerformance, setLoadingPerformance] = useState(false);

  // Action loading states
  const [actionLoading, setActionLoading] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    product_ids: [],
    discount_percentage: 10,
    start_date: '',
    end_date: '',
    channel: 'in-app',
    whatsapp_template: '',
  });

  // Product search in modal
  const [productSearch, setProductSearch] = useState('');

  // ---------- Data Loading ----------

  const loadCampaigns = useCallback(async () => {
    if (!selectedStore) return;
    try {
      setLoading(true);
      setError(null);
      const response = await campaignsAPI.list(selectedStore.id);
      setCampaigns(response.data?.campaigns || response.data || []);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load campaigns'));
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  const loadDeadStock = useCallback(async () => {
    if (!selectedStore) return;
    try {
      setLoadingDeadStock(true);
      const response = await analyticsAPI.getDeadStock(selectedStore.id);
      const data = response.data?.products || response.data?.items || response.data;
      setDeadStockProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load dead stock:', err);
      setDeadStockProducts([]);
    } finally {
      setLoadingDeadStock(false);
    }
  }, [selectedStore]);

  useEffect(() => {
    if (selectedStore) {
      loadCampaigns();
    }
  }, [selectedStore, loadCampaigns]);

  // ---------- Helpers ----------

  const clearMessages = () => {
    setTimeout(() => setSuccessMsg(null), 4000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    if (amount == null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: selectedStore?.currency || 'SAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const map = {
      draft: 'badge badge-info',
      active: 'badge badge-success',
      expired: 'badge badge-secondary',
      cancelled: 'badge badge-danger',
    };
    return map[status] || 'badge badge-info';
  };

  // ---------- Create Campaign ----------

  const openCreateModal = () => {
    setFormData({
      name: '',
      description: '',
      product_ids: [],
      discount_percentage: 10,
      start_date: '',
      end_date: '',
      channel: 'in-app',
      whatsapp_template: '',
    });
    setProductSearch('');
    setShowCreateModal(true);
    loadDeadStock();
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleProductSelection = (productId) => {
    setFormData((prev) => {
      const ids = prev.product_ids.includes(productId)
        ? prev.product_ids.filter((id) => id !== productId)
        : [...prev.product_ids, productId];
      return { ...prev, product_ids: ids };
    });
  };

  const selectAllFiltered = () => {
    const filtered = getFilteredDeadStock();
    const allIds = filtered.map((p) => p.product_id || p.id);
    setFormData((prev) => {
      const existing = new Set(prev.product_ids);
      allIds.forEach((id) => existing.add(id));
      return { ...prev, product_ids: Array.from(existing) };
    });
  };

  const deselectAll = () => {
    setFormData((prev) => ({ ...prev, product_ids: [] }));
  };

  const getFilteredDeadStock = () => {
    const products = Array.isArray(deadStockProducts) ? deadStockProducts : [];
    if (!productSearch.trim()) return products;
    const term = productSearch.toLowerCase();
    return products.filter(
      (p) =>
        (p.product_name || p.name || '').toLowerCase().includes(term) ||
        (p.sku || '').toLowerCase().includes(term)
    );
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    if (formData.product_ids.length === 0) {
      setError('Please select at least one product');
      return;
    }
    if (!formData.name.trim()) {
      setError('Please enter a campaign name');
      return;
    }
    try {
      setCreating(true);
      setError(null);
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        product_ids: formData.product_ids,
        discount_percentage: parseFloat(formData.discount_percentage),
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        channel: formData.channel,
        whatsapp_template: formData.whatsapp_template.trim() || null,
      };
      await campaignsAPI.create(selectedStore.id, payload);
      setShowCreateModal(false);
      setSuccessMsg('Campaign created successfully');
      clearMessages();
      await loadCampaigns();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to create campaign'));
    } finally {
      setCreating(false);
    }
  };

  // ---------- Campaign Actions ----------

  const handleActivate = async (campaignId) => {
    try {
      setActionLoading((prev) => ({ ...prev, [campaignId]: 'activate' }));
      setError(null);
      await campaignsAPI.activate(selectedStore.id, campaignId);
      setSuccessMsg('Campaign activated - discounted prices are now live');
      clearMessages();
      await loadCampaigns();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to activate campaign'));
    } finally {
      setActionLoading((prev) => ({ ...prev, [campaignId]: null }));
    }
  };

  const handleSendWhatsApp = async (campaignId) => {
    try {
      setActionLoading((prev) => ({ ...prev, [campaignId]: 'whatsapp' }));
      setError(null);
      await campaignsAPI.sendWhatsApp(selectedStore.id, campaignId);
      setSuccessMsg('WhatsApp messages sent successfully');
      clearMessages();
      await loadCampaigns();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to send WhatsApp messages'));
    } finally {
      setActionLoading((prev) => ({ ...prev, [campaignId]: null }));
    }
  };

  const handleExpire = async (campaignId) => {
    if (!window.confirm('Expire this campaign? Original prices will be restored.')) return;
    try {
      setActionLoading((prev) => ({ ...prev, [campaignId]: 'expire' }));
      setError(null);
      await campaignsAPI.expire(selectedStore.id, campaignId);
      setSuccessMsg('Campaign expired - prices restored to original');
      clearMessages();
      await loadCampaigns();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to expire campaign'));
    } finally {
      setActionLoading((prev) => ({ ...prev, [campaignId]: null }));
    }
  };

  const handleViewPerformance = async (campaign) => {
    try {
      setPerformanceCampaign(campaign);
      setLoadingPerformance(true);
      setPerformanceData(null);
      const response = await campaignsAPI.getPerformance(selectedStore.id, campaign.id);
      setPerformanceData(response.data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load performance data'));
      setPerformanceCampaign(null);
    } finally {
      setLoadingPerformance(false);
    }
  };

  const closePerformance = () => {
    setPerformanceCampaign(null);
    setPerformanceData(null);
  };

  // ---------- Render: No Store ----------

  if (!selectedStore) {
    return (
      <div className="page-container">
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: '48px 24px' }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>
              Please select a store to manage campaigns.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Render: Performance View ----------

  if (performanceCampaign) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div>
            <h1 className="page-title">Campaign Performance</h1>
            <p style={{ color: 'var(--text-secondary)', margin: '4px 0 0' }}>
              {performanceCampaign.name}
            </p>
          </div>
          <button className="btn btn-secondary" onClick={closePerformance}>
            Back to Campaigns
          </button>
        </div>

        {loadingPerformance ? (
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '48px 24px' }}>
              <div className="spinner" />
              <p style={{ color: 'var(--text-secondary)', marginTop: '12px' }}>
                Loading performance data...
              </p>
            </div>
          </div>
        ) : performanceData ? (
          <>
            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div className="card">
                <div className="card-body" style={{ textAlign: 'center' }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '4px' }}>
                    Total Units Sold
                  </p>
                  <p style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>
                    {performanceData.total_units_sold ?? 0}
                  </p>
                </div>
              </div>
              <div className="card">
                <div className="card-body" style={{ textAlign: 'center' }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '4px' }}>
                    Total Revenue
                  </p>
                  <p style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>
                    {formatCurrency(performanceData.total_revenue)}
                  </p>
                </div>
              </div>
              <div className="card">
                <div className="card-body" style={{ textAlign: 'center' }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '4px' }}>
                    Avg. Discount
                  </p>
                  <p style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>
                    {performanceData.discount_percentage ?? performanceCampaign.discount_percentage ?? 0}%
                  </p>
                </div>
              </div>
              <div className="card">
                <div className="card-body" style={{ textAlign: 'center' }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '4px' }}>
                    Products in Campaign
                  </p>
                  <p style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>
                    {performanceData.products?.length ?? performanceCampaign.products_count ?? 0}
                  </p>
                </div>
              </div>
            </div>

            {/* Per-Product Breakdown */}
            <div className="card">
              <div className="card-header">
                <h3 style={{ fontSize: '16px', fontWeight: '600' }}>Per-Product Breakdown</h3>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                <div style={{ overflowX: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr className="table-header">
                        <th>Product</th>
                        <th>SKU</th>
                        <th style={{ textAlign: 'right' }}>Original Price</th>
                        <th style={{ textAlign: 'right' }}>Offer Price</th>
                        <th style={{ textAlign: 'right' }}>Units Sold</th>
                        <th style={{ textAlign: 'right' }}>Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(performanceData.products || []).length === 0 ? (
                        <tr>
                          <td colSpan="6" style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
                            No product data available yet
                          </td>
                        </tr>
                      ) : (
                        performanceData.products.map((item, idx) => (
                          <tr key={idx} className="table-row">
                            <td style={{ fontWeight: '500' }}>{item.product_name || item.name}</td>
                            <td style={{ color: 'var(--text-secondary)' }}>{item.sku || '-'}</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(item.original_price)}</td>
                            <td style={{ textAlign: 'right', color: 'var(--success)' }}>{formatCurrency(item.offer_price)}</td>
                            <td style={{ textAlign: 'right', fontWeight: '600' }}>{item.units_sold ?? 0}</td>
                            <td style={{ textAlign: 'right', fontWeight: '600' }}>{formatCurrency(item.revenue)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '48px 24px' }}>
              <p style={{ color: 'var(--text-secondary)' }}>No performance data available.</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---------- Render: Main Campaign List ----------

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Offer Campaigns</h1>
          <p style={{ color: 'var(--text-secondary)', margin: '4px 0 0', fontSize: '14px' }}>
            Create discount campaigns for dead stock and slow-moving inventory
          </p>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + New Campaign
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="card" style={{ borderLeft: '4px solid var(--danger)', marginBottom: '16px' }}>
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px' }}>
            <span style={{ color: 'var(--danger)', fontSize: '14px' }}>{error}</span>
            <button
              onClick={() => setError(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '18px' }}
            >
              x
            </button>
          </div>
        </div>
      )}
      {successMsg && (
        <div className="card" style={{ borderLeft: '4px solid var(--success)', marginBottom: '16px' }}>
          <div className="card-body" style={{ padding: '12px 16px' }}>
            <span style={{ color: 'var(--success)', fontSize: '14px' }}>{successMsg}</span>
          </div>
        </div>
      )}

      {/* Campaign Table */}
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px 24px' }}>
              <div className="spinner" />
              <p style={{ color: 'var(--text-secondary)', marginTop: '12px' }}>Loading campaigns...</p>
            </div>
          ) : campaigns.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px' }}>
              <p style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>
                No campaigns yet
              </p>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '20px' }}>
                Create your first offer campaign to start clearing dead stock
              </p>
              <button className="btn btn-primary" onClick={openCreateModal}>
                + Create Campaign
              </button>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr className="table-header">
                    <th>Name</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Discount</th>
                    <th style={{ textAlign: 'right' }}>Products</th>
                    <th style={{ textAlign: 'right' }}>Units Sold</th>
                    <th style={{ textAlign: 'right' }}>Revenue</th>
                    <th>Channel</th>
                    <th>Start</th>
                    <th>End</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign) => (
                    <tr key={campaign.id} className="table-row">
                      <td>
                        <span
                          style={{ fontWeight: '500', cursor: 'pointer', color: 'var(--primary)' }}
                          onClick={() => handleViewPerformance(campaign)}
                        >
                          {campaign.name}
                        </span>
                      </td>
                      <td>
                        <span className={getStatusBadge(campaign.status)}>
                          {campaign.status}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>{campaign.discount_percentage}%</td>
                      <td style={{ textAlign: 'right' }}>{campaign.products_count ?? campaign.product_ids?.length ?? 0}</td>
                      <td style={{ textAlign: 'right', fontWeight: '600' }}>{campaign.units_sold ?? 0}</td>
                      <td style={{ textAlign: 'right', fontWeight: '600' }}>{formatCurrency(campaign.revenue ?? 0)}</td>
                      <td>
                        <span className="badge badge-info" style={{ textTransform: 'capitalize' }}>
                          {campaign.channel || 'in-app'}
                        </span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(campaign.start_date)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(campaign.end_date)}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                          {campaign.status === 'draft' && (
                            <button
                              className="btn btn-success"
                              style={{ fontSize: '12px', padding: '4px 10px' }}
                              onClick={() => handleActivate(campaign.id)}
                              disabled={!!actionLoading[campaign.id]}
                            >
                              {actionLoading[campaign.id] === 'activate' ? 'Activating...' : 'Activate'}
                            </button>
                          )}
                          {(campaign.status === 'active' && (campaign.channel === 'whatsapp' || campaign.channel === 'both')) && (
                            <button
                              className="btn btn-success"
                              style={{ fontSize: '12px', padding: '4px 10px' }}
                              onClick={() => handleSendWhatsApp(campaign.id)}
                              disabled={!!actionLoading[campaign.id]}
                            >
                              {actionLoading[campaign.id] === 'whatsapp' ? 'Sending...' : 'WhatsApp'}
                            </button>
                          )}
                          {campaign.status === 'active' && (
                            <button
                              className="btn btn-danger"
                              style={{ fontSize: '12px', padding: '4px 10px' }}
                              onClick={() => handleExpire(campaign.id)}
                              disabled={!!actionLoading[campaign.id]}
                            >
                              {actionLoading[campaign.id] === 'expire' ? 'Expiring...' : 'Expire'}
                            </button>
                          )}
                          <button
                            className="btn btn-secondary"
                            style={{ fontSize: '12px', padding: '4px 10px' }}
                            onClick={() => handleViewPerformance(campaign)}
                          >
                            Performance
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '640px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className="modal-header">
              <h2 style={{ fontSize: '18px', fontWeight: '600' }}>Create Offer Campaign</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--text-secondary)' }}
              >
                x
              </button>
            </div>

            <form onSubmit={handleCreateCampaign}>
              <div className="modal-body" style={{ overflowY: 'auto', maxHeight: 'calc(90vh - 140px)' }}>
                {/* Campaign Name */}
                <div className="form-group">
                  <label className="form-label">Campaign Name *</label>
                  <input
                    className="form-input"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    placeholder="e.g., Summer Dead Stock Clearance"
                    required
                  />
                </div>

                {/* Description */}
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-input"
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    placeholder="Brief description of this campaign..."
                    rows={2}
                    style={{ resize: 'vertical' }}
                  />
                </div>

                {/* Discount & Channel Row */}
                <div className="campaigns-modal-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label className="form-label">Discount Percentage *</label>
                    <input
                      className="form-input"
                      type="number"
                      name="discount_percentage"
                      value={formData.discount_percentage}
                      onChange={handleFormChange}
                      min="1"
                      max="100"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Channel</label>
                    <select
                      className="form-select"
                      name="channel"
                      value={formData.channel}
                      onChange={handleFormChange}
                    >
                      <option value="in-app">In-App Only</option>
                      <option value="whatsapp">WhatsApp Only</option>
                      <option value="both">Both</option>
                    </select>
                  </div>
                </div>

                {/* Dates Row */}
                <div className="campaigns-modal-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label className="form-label">Start Date</label>
                    <input
                      className="form-input"
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">End Date</label>
                    <input
                      className="form-input"
                      type="date"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                {/* WhatsApp Template */}
                {(formData.channel === 'whatsapp' || formData.channel === 'both') && (
                  <div className="form-group">
                    <label className="form-label">WhatsApp Message Template</label>
                    <textarea
                      className="form-input"
                      name="whatsapp_template"
                      value={formData.whatsapp_template}
                      onChange={handleFormChange}
                      placeholder={"Hi {customer_name}, we have a special {discount}% offer on select items! Check it out now."}
                      rows={3}
                      style={{ resize: 'vertical' }}
                    />
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                      Available variables: {'{customer_name}'}, {'{discount}'}, {'{store_name}'}, {'{campaign_name}'}
                    </p>
                  </div>
                )}

                {/* Product Selection */}
                <div className="form-group">
                  <label className="form-label">
                    Select Dead Stock Products * ({formData.product_ids.length} selected)
                  </label>

                  {loadingDeadStock ? (
                    <div style={{ textAlign: 'center', padding: '24px' }}>
                      <div className="spinner" />
                      <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginTop: '8px' }}>
                        Loading dead stock products...
                      </p>
                    </div>
                  ) : deadStockProducts.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px', background: 'var(--bg-secondary, #f8fafc)', borderRadius: '8px' }}>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                        No dead stock products found. Run analytics recalculation or check your inventory.
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Search & Bulk Actions */}
                      <div className="campaigns-product-actions" style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <input
                          className="form-input"
                          type="text"
                          placeholder="Search products..."
                          value={productSearch}
                          onChange={(e) => setProductSearch(e.target.value)}
                          style={{ flex: 1 }}
                        />
                        <button type="button" className="btn btn-secondary" style={{ fontSize: '12px', whiteSpace: 'nowrap' }} onClick={selectAllFiltered}>
                          Select All
                        </button>
                        <button type="button" className="btn btn-secondary" style={{ fontSize: '12px', whiteSpace: 'nowrap' }} onClick={deselectAll}>
                          Clear
                        </button>
                      </div>

                      {/* Product Checkbox List */}
                      <div
                        style={{
                          maxHeight: '200px',
                          overflowY: 'auto',
                          border: '1px solid var(--border-color, #e2e8f0)',
                          borderRadius: '8px',
                        }}
                      >
                        {getFilteredDeadStock().map((product) => {
                          const pid = product.product_id || product.id;
                          const isSelected = formData.product_ids.includes(pid);
                          return (
                            <label
                              key={pid}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '10px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid var(--border-color, #e2e8f0)',
                                background: isSelected ? 'var(--primary-light, rgba(108, 99, 255, 0.06))' : 'transparent',
                                transition: 'background 0.12s ease-out',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleProductSelection(pid)}
                                style={{ accentColor: 'var(--primary)' }}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ fontSize: '14px', fontWeight: '500', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {product.product_name || product.name}
                                </p>
                                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '2px 0 0' }}>
                                  {product.sku ? `SKU: ${product.sku}` : ''}
                                  {product.sku && product.days_without_sale != null ? ' | ' : ''}
                                  {product.days_without_sale != null ? `${product.days_without_sale} days without sale` : ''}
                                  {product.current_stock != null ? ` | Stock: ${product.current_stock}` : ''}
                                </p>
                              </div>
                              {product.selling_price != null && (
                                <span style={{ fontSize: '13px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                                  {formatCurrency(product.selling_price)}
                                </span>
                              )}
                            </label>
                          );
                        })}
                        {getFilteredDeadStock().length === 0 && (
                          <p style={{ textAlign: 'center', padding: '16px', color: 'var(--text-secondary)', fontSize: '13px' }}>
                            No products match your search
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={creating || formData.product_ids.length === 0}
                >
                  {creating ? 'Creating...' : 'Create Campaign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Campaigns;
